import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  FormSection,
  IconButton,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import ReactMarkdown from "react-markdown";

import { DialogProps, DisplayTokenDialogProps } from "./types";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";

export const DisplayOnceTokenDialog = ({
  isOpen,
  onUserDismiss,
  createdToken,
}: DisplayTokenDialogProps) => {
  if (!createdToken) {
    return null;
  }

  const { sendNotification } = useNotificationContext();

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text);
    sendNotification({
      type: "dismissible_success",
      text: "Text copied.",
    });
  };

  return (
    <Dialog
      title="Your API token"
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      width="600px"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={() => copyText(createdToken.token)}>
            Copy Token
          </Button>
        </div>
      }
    >
      <div style={{ fontSize: "var(--font-size-s)", margin: "10px 0" }}>
        <span>
          For security reasons, this is the only time we will display this token
          and header secret to you. You will need to generate a new token if you
          lose this one.
        </span>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          fontWeight: "bold",
          color: "var(--color-black)",
        }}
      >
        <FormSection title="Bearer Token" />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "var(--bgColor-smokeGrey)",
            padding: "10px",
            width: "540px",
          }}
        >
          <span>{createdToken?.token}</span>
          <IconButton
            name="copy"
            onClick={() => copyText(createdToken?.token || "")}
          />
        </div>
        <FormSection title="Header Secret" />
        <div
          style={{
            display: "flex",
            backgroundColor: "var(--bgColor-smokeGrey)",
            padding: "10px",
            width: "540px",
          }}
        >
          <span
            style={{
              width: "500px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {createdToken?.secret}
          </span>
          <IconButton
            name="copy"
            onClick={() => copyText(createdToken?.secret || "")}
          />
        </div>
      </div>
    </Dialog>
  );
};

export const TermsOfUseDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
}: DialogProps) => {
  const [hasAgreed, setHasAgreed] = useState(false);
  const [alertEl, setAlertEl] = useState<React.ReactNode>(null);
  const institutionSettings = useInstitutionSettings();

  const onConfirmInner = () => {
    setAlertEl(null);
    if (hasAgreed) {
      onConfirm();
    } else {
      setAlertEl(
        <Alert isActive kind="error">
          Must accept terms.
        </Alert>,
      );
    }
  };

  const dialogFooter = (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        style={{ paddingRight: "10px" }}
        kind="plain"
        onClick={onUserDismiss}
      >
        Cancel
      </Button>
      <Button onClick={onConfirmInner}>Confirm</Button>
    </div>
  );

  return (
    <Dialog
      title="Review terms of use"
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      notification={alertEl}
      footer={dialogFooter}
    >
      <div style={{ whiteSpace: "pre-wrap" }}>
        <ReactMarkdown>
          {institutionSettings.api_banking_agreement as string}
        </ReactMarkdown>
      </div>
      <br />
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <Checkbox
          label="I accept the"
          name="agree"
          checked={hasAgreed}
          onChange={() => setHasAgreed(!hasAgreed)}
        />
        <span>&nbsp;</span>
        <a
          href="https://www.narmi.com/policies/developer-terms-conditions"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Use
        </a>
      </div>
    </Dialog>
  );
};
